import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { dictionaries, type Language, type TranslationKey } from './translations';

// Interpolation params for a translated message. Values are substituted into
// `{name}` placeholders (e.g. `t('projects.form.nameHint', { min: 1, max: 100 })`).
export type TranslationParams = Readonly<Record<string, string | number>>;

// The shape exposed by `useT()`: the translator, the current language, and a
// setter that also persists the choice.
export interface I18n {
  /** Translate a key, substituting any `{param}` placeholders. */
  readonly t: (key: TranslationKey, params?: TranslationParams) => string;
  /** The active language. */
  readonly lang: Language;
  /** Switch the active language (persisted to localStorage). */
  readonly setLang: (lang: Language) => void;
}

// Default language is English (explicit — no browser auto-detection).
const DEFAULT_LANGUAGE: Language = 'en';

// localStorage key under which the selected language is persisted.
const STORAGE_KEY = 'courseai.lang';

const I18nContext = createContext<I18n | undefined>(undefined);

function isLanguage(value: string | null): value is Language {
  return value === 'en' || value === 'es';
}

// Read the persisted language on init. Falls back to English when absent,
// invalid, or when storage is unavailable (e.g. SSR/private mode).
function readInitialLanguage(): Language {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isLanguage(stored)) {
      return stored;
    }
  } catch {
    // Storage unavailable — fall through to the default.
  }
  return DEFAULT_LANGUAGE;
}

// Substitute `{param}` placeholders in a template with the provided values.
function interpolate(template: string, params?: TranslationParams): string {
  if (params === undefined) {
    return template;
  }
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
}

interface I18nProviderProps {
  readonly children: ReactNode;
}

// Provider holding the current language + a persisting setter. Wrap the app in
// this so every screen can call `useT()`. Switching the language updates
// context (re-rendering consumers immediately) and writes the choice to
// localStorage so it survives reloads.
export function I18nProvider({ children }: I18nProviderProps): ReactNode {
  const [lang, setLangState] = useState<Language>(readInitialLanguage);

  const setLang = useCallback((next: Language): void => {
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Ignore storage failures — the in-memory selection still applies.
    }
  }, []);

  const value = useMemo<I18n>(() => {
    const dictionary = dictionaries[lang];
    return {
      lang,
      setLang,
      t: (key, params) => interpolate(dictionary[key], params),
    };
  }, [lang, setLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

// Access the i18n API. Must be called under an <I18nProvider />.
export function useT(): I18n {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useT must be used within an I18nProvider');
  }
  return context;
}
